/**
 * Feature Registry: Central catalog of all application features, modules, and data resources.
 * Each entry configures default enablement, online/offline mode, read/write permissions, and associated API endpoints.
 */

export const FEATURE_GROUPS = {
  CORE: 'Core & Identity',
  ACADEMICS: 'Academics & Timetable',
  ATTENDANCE: 'Biometrics & Attendance',
  COMMUNICATION: 'Communication & Notices',
  FINANCE: 'Finance & Payments',
  HARDWARE: 'Terminals & Devices',
  REPORTS: 'Reports & Exports',
  OPERATIONS: 'Exhibition & Operations'
};

export const FEATURES = [
  // Core & Identity
  {
    id: 'auth_users',
    label: 'Login & Demo Accounts',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: ['/auth/demo-users', '/auth/me'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Roster of seeded demo users across Principal, Teacher, Student, and Parent roles.'
  },
  {
    id: 'students',
    label: 'Students Directory',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: ['/students'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Student profiles, admission details, and parent link associations.'
  },
  {
    id: 'teachers',
    label: 'Teachers & Faculty',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: ['/teachers'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Faculty directory, employee codes, subjects, and classroom assignments.'
  },
  {
    id: 'parents',
    label: 'Parents Directory',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: ['/parents'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Parent contacts, communication numbers, and linked pupil accounts.'
  },
  {
    id: 'dashboards',
    label: 'Role Dashboards',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: ['/dashboard'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Summary KPIs, stats, and personalized homeviews for each role.'
  },
  {
    id: 'user_switcher',
    label: 'Quick User Switcher',
    group: FEATURE_GROUPS.CORE,
    apiPrefixes: [],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Instant demo user impersonation switcher in Topbar and drawer.'
  },

  // Academics & Timetable
  {
    id: 'classes',
    label: 'Classes & Sections',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/classes'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Classrooms 6 through 10, sections A/B, and designated class teachers.'
  },
  {
    id: 'subjects',
    label: 'Subjects Curriculum',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/subjects'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Academic subjects curriculum (Mathematics, Science, English, etc.).'
  },
  {
    id: 'timetable',
    label: 'Weekly Timetable',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/timetable'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Bell schedule, daily class periods, room allocations, and faculty assignments.'
  },
  {
    id: 'homework',
    label: 'Homework & Assignments',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/homework'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Classroom homework assignments, due dates, and task descriptions.'
  },
  {
    id: 'homework_submissions',
    label: 'Homework Submissions',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/homework/submissions'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Student homework submission status, text uploads, and grading.'
  },
  {
    id: 'exams_marks',
    label: 'Exams & Marks Entry',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/exams', '/marks'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Examination schedules, max marks criteria, and bulk score entry.'
  },
  {
    id: 'results',
    label: 'Student Report Cards',
    group: FEATURE_GROUPS.ACADEMICS,
    apiPrefixes: ['/results'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Term report cards, subject breakdowns, grades, and trend charts.'
  },

  // Biometrics & Attendance
  {
    id: 'attendance_today',
    label: 'Today Live Attendance',
    group: FEATURE_GROUPS.ATTENDANCE,
    apiPrefixes: ['/attendance', '/attendance/staff'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Today’s classroom rosters, teacher attendance check-in, and manual adjustments.'
  },
  {
    id: 'attendance_history',
    label: 'Attendance History & Summary',
    group: FEATURE_GROUPS.ATTENDANCE,
    apiPrefixes: ['/attendance/summary', '/attendance/student'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Monthly student calendar logs, percentages, and defaulter tracking (<75%).'
  },
  {
    id: 'live_feed',
    label: 'Live Biometric Feed',
    group: FEATURE_GROUPS.ATTENDANCE,
    apiPrefixes: ['/attendance/live'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Recent gate telemetry punch stream with direction indicators.'
  },
  {
    id: 'gate_display',
    label: 'Kiosk Gate Display',
    group: FEATURE_GROUPS.ATTENDANCE,
    apiPrefixes: ['/attendance/live'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'High-contrast projector screen display for school gate arrival monitoring.'
  },
  {
    id: 'leaves',
    label: 'Leave Applications',
    group: FEATURE_GROUPS.ATTENDANCE,
    apiPrefixes: ['/leaves'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Leave request submission, student absence logs, and principal approvals.'
  },

  // Hardware & Devices
  {
    id: 'devices',
    label: 'Optical Scanners & Devices',
    group: FEATURE_GROUPS.HARDWARE,
    apiPrefixes: ['/devices', '/device', '/fingerprints', '/demo/reset'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'ESP32 terminal registration, heartbeat status, and optical sensor slot mapping.'
  },
  {
    id: 'simulate_punch',
    label: 'Biometric Punch Simulator',
    group: FEATURE_GROUPS.HARDWARE,
    apiPrefixes: ['/demo/simulate-punch'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Operator punch injection tool mimicking physical finger press.'
  },

  // Communication & Notices
  {
    id: 'notices',
    label: 'Notice Board & Circulars',
    group: FEATURE_GROUPS.COMMUNICATION,
    apiPrefixes: ['/notices'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Broadcast school circulars, pinned bulletins, and audience-targeted notices.'
  },
  {
    id: 'emergency_banner',
    label: 'Emergency Broadcast Banner',
    group: FEATURE_GROUPS.COMMUNICATION,
    apiPrefixes: ['/notices'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Urgent red alert banner rendered across all client views.'
  },
  {
    id: 'events',
    label: 'Events & Holiday Calendar',
    group: FEATURE_GROUPS.COMMUNICATION,
    apiPrefixes: ['/events'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Campus events, sports day schedules, and gazetted school holidays.'
  },
  {
    id: 'chat',
    label: 'Messenger & Class Chat',
    group: FEATURE_GROUPS.COMMUNICATION,
    apiPrefixes: ['/chat'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Direct 1:1 messaging, faculty room channels, and parent-teacher groups.'
  },
  {
    id: 'notifications_bell',
    label: 'Notifications & Alerts',
    group: FEATURE_GROUPS.COMMUNICATION,
    apiPrefixes: ['/notifications'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Automated arrival alerts, fee settlement reminders, and unread counts.'
  },

  // Finance & Payments
  {
    id: 'fees_structures',
    label: 'Fee Structures & Heads',
    group: FEATURE_GROUPS.FINANCE,
    apiPrefixes: ['/fees/structures'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Tuition rates, lab fees, computer fees, and sports fund allocations.'
  },
  {
    id: 'invoices',
    label: 'Student Fee Invoices',
    group: FEATURE_GROUPS.FINANCE,
    apiPrefixes: ['/fees/invoices'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Term-wise invoices, outstanding dues balances, and payment status.'
  },
  {
    id: 'fee_payments',
    label: 'Fee Payments & Gateway',
    group: FEATURE_GROUPS.FINANCE,
    apiPrefixes: ['/fees/invoices'],
    supportsDemo: true,
    writable: true,
    defaultMode: 'live',
    defaultAccess: 'readwrite',
    defaultEnabled: true,
    description: 'Mock UPI / Card payment settlement and instant transaction confirmation.'
  },
  {
    id: 'receipts',
    label: 'Payment Receipts',
    group: FEATURE_GROUPS.FINANCE,
    apiPrefixes: ['/fees/receipts'],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Official digital fee receipts with transaction IDs and print layouts.'
  },

  // Reports & Identity Card
  {
    id: 'digital_id_card',
    label: 'Student Digital ID Card',
    group: FEATURE_GROUPS.REPORTS,
    apiPrefixes: [],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Printable student holographic identity card with barcode and details.'
  },
  {
    id: 'report_card_print',
    label: 'Printable Report Card',
    group: FEATURE_GROUPS.REPORTS,
    apiPrefixes: [],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Official academic transcript print preview with school header and grading.'
  },
  {
    id: 'csv_export',
    label: 'Data CSV Exporter',
    group: FEATURE_GROUPS.REPORTS,
    apiPrefixes: [],
    supportsDemo: true,
    writable: false,
    defaultMode: 'live',
    defaultAccess: 'readonly',
    defaultEnabled: true,
    description: 'Export attendance rosters and fee ledgers into downloadable spreadsheets.'
  }
];

export const FEATURE_MAP = new Map(FEATURES.map((f) => [f.id, f]));

/**
 * Find the matching feature ID for a given API URL path.
 * @param {string} path 
 * @returns {string|null}
 */
export function findFeatureByPath(path) {
  if (!path) return null;
  const cleanPath = path.replace(/^\/api/, '');
  for (const f of FEATURES) {
    for (const prefix of f.apiPrefixes) {
      if (cleanPath === prefix || cleanPath.startsWith(`${prefix}/`) || cleanPath.startsWith(`${prefix}?`)) {
        return f.id;
      }
    }
  }
  return null;
}
